import { Button, Checkbox, Dialog, Input, Select } from '@artosera/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDossierArtiste } from '../../api/cvArtiste.js';
import { useOeuvres } from '../../api/oeuvres.js';
import { ouvrirUrlSignee } from '../../api/client.js';
import { estApiError } from '../../api/errors.js';
import { Field } from '../Field.js';
import { VignetteOeuvre } from '../../composants/VignetteOeuvre.js';
import styles from './DossierArtiste.module.css';

export function DossierArtisteDialog({
  galerieSlug,
  artisteId,
  fermer,
}: {
  galerieSlug: string;
  artisteId: string;
  fermer: () => void;
}) {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const [langue, setLangue] = useState<'fr' | 'en'>(i18n.language === 'en' ? 'en' : 'fr');
  const [ids, setIds] = useState<string[]>([]);
  const [glisse, setGlisse] = useState<string | null>(null);
  const [avecPrix, setAvecPrix] = useState(false);
  const [avecPresse, setAvecPresse] = useState(false);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const oeuvres = useOeuvres(galerieSlug, { artisteId, parPage: 30, page, q, vignettes: true });
  const dossier = useDossierArtiste(galerieSlug, artisteId);
  const [erreur, setErreur] = useState<string | null>(null);
  const [choisies, setChoisies] = useState<
    Record<string, { titre: string; vignette: string | null }>
  >({});
  const tr = (cle: string) => t(`regie:dossierArtiste.${cle}`);
  function deplacer(id: string, cible: string) {
    if (id === cible) return;
    setIds((avant) => {
      const apres = [...avant];
      const depart = apres.indexOf(id),
        arrivee = apres.indexOf(cible);
      if (depart < 0 || arrivee < 0) return avant;
      apres.splice(arrivee, 0, ...apres.splice(depart, 1));
      return apres;
    });
  }
  async function telecharger() {
    setErreur(null);
    try {
      const pdf = await dossier.mutateAsync({ langue, oeuvreIds: ids, avecPrix, avecPresse });
      ouvrirUrlSignee(pdf.url);
      fermer();
    } catch (e) {
      setErreur(estApiError(e) ? e.message : t('commun:erreurReseau'));
    }
  }
  return (
    <Dialog
      open
      onOpenChange={(ouvert) => {
        if (!ouvert) fermer();
      }}
      title={tr('titre')}
      closeLabel={t('commun:actions.fermer')}
      footer={
        <Button loading={dossier.isPending} onClick={() => void telecharger()}>
          {tr('telecharger')}
        </Button>
      }
    >
      <div className={styles.formulaire}>
        <Field label={tr('langue')} htmlFor="dossier-artiste-langue">
          <Select
            id="dossier-artiste-langue"
            value={langue}
            onValueChange={(v) => setLangue(v as 'fr' | 'en')}
            options={[
              { value: 'fr', label: 'Français' },
              { value: 'en', label: 'English' },
            ]}
          />
        </Field>
        <Checkbox
          checked={avecPrix}
          onCheckedChange={(v) => setAvecPrix(v === true)}
          label={tr('prix')}
        />
        <Checkbox
          checked={avecPresse}
          onCheckedChange={(v) => setAvecPresse(v === true)}
          label={tr('presse')}
        />
        <p>
          {tr('selection')} ({ids.length}/20)
        </p>
        <ol className={styles.liste}>
          {ids.map((id, rang) => (
            <li
              key={id}
              className={styles.entree}
              draggable
              onDragStart={() => setGlisse(id)}
              onDragEnd={() => setGlisse(null)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => {
                if (glisse) deplacer(glisse, id);
              }}
            >
              <VignetteOeuvre url={choisies[id]?.vignette ?? null} taille="sm" />
              <span>{choisies[id]?.titre}</span>
              <div className={styles.actions}>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={rang === 0}
                  onClick={() => deplacer(id, ids[rang - 1]!)}
                >
                  {tr('monter')}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={rang === ids.length - 1}
                  onClick={() => deplacer(id, ids[rang + 1]!)}
                >
                  {tr('descendre')}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIds((avant) => avant.filter((i) => i !== id))}
                >
                  {tr('retirer')}
                </Button>
              </div>
            </li>
          ))}
        </ol>
        <Field label={tr('rechercher')} htmlFor="dossier-artiste-recherche">
          <Input
            id="dossier-artiste-recherche"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
          />
        </Field>
        {oeuvres.isLoading && <p>{t('commun:chargement')}</p>}
        {oeuvres.error && (
          <p role="alert">
            {estApiError(oeuvres.error) ? oeuvres.error.message : t('commun:erreurReseau')}
          </p>
        )}
        <ul className={styles.liste}>
          {oeuvres.data?.oeuvres.map((oeuvre) => (
            <li key={oeuvre.id} className={styles.entree}>
              <VignetteOeuvre url={oeuvres.data?.vignettes?.urls[oeuvre.id] ?? null} taille="sm" />
              <Checkbox
                label={oeuvre.titre}
                checked={ids.includes(oeuvre.id)}
                disabled={!ids.includes(oeuvre.id) && ids.length >= 20}
                onCheckedChange={(v) => {
                  setIds((avant) =>
                    v === true ? [...avant, oeuvre.id] : avant.filter((i) => i !== oeuvre.id),
                  );
                  setChoisies((avant) => ({
                    ...avant,
                    [oeuvre.id]: {
                      titre: oeuvre.titre,
                      vignette: oeuvres.data?.vignettes?.urls[oeuvre.id] ?? null,
                    },
                  }));
                }}
              />
            </li>
          ))}
        </ul>
        <div className={styles.actions}>
          <Button
            variant="ghost"
            disabled={page === 1 || oeuvres.isFetching}
            onClick={() => setPage((p) => p - 1)}
          >
            {tr('precedent')}
          </Button>
          <Button
            variant="ghost"
            disabled={!oeuvres.data || page >= oeuvres.data.pagination.pages || oeuvres.isFetching}
            onClick={() => setPage((p) => p + 1)}
          >
            {tr('suivant')}
          </Button>
        </div>
        {erreur && <p role="alert">{erreur}</p>}
      </div>
    </Dialog>
  );
}
